// BankLearningTab — 銀行取引 学習機構 (= schema-v33 / 2026-05-08)
//
// 既存 LearningPage の主要内容 (= Top Patterns / 品質問題 / 手動学習) を
// 「銀行取引」 Level 1 タブ配下の 3 sub-tab として 切り出したコンポーネント。

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  CardBase,
  Button,
  Note,
  TabBar,
  MessageBlock,
  TextField,
  TextArea,
  SelectBox,
  VStack,
  InlineSpinner,
  SubSectionTitle,
} from '@freee_jp/vibes';
import {
  fetchLearningMetrics,
  fetchLearningTopPatterns,
  fetchLearningQualityIssues,
  postLearningChatIngest,
  postMonthlyPatternRebuild,
  LearningMetrics,
  LearningPattern,
  LearningQualityIssue,
} from '../../api/client';

const formatPct = (n: number | null | undefined) =>
  n == null ? '—' : `${(n * 100).toFixed(1)}%`;

export const BankLearningTab: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [metrics, setMetrics] = useState<LearningMetrics | null>(null);
  const [topPatterns, setTopPatterns] = useState<LearningPattern[]>([]);
  const [issues, setIssues] = useState<{
    high_rejection_rate: LearningQualityIssue[];
    stale_patterns: LearningQualityIssue[];
    low_confidence_high_sample: LearningQualityIssue[];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [rebuilding, setRebuilding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [mRes, pRes, qRes] = await Promise.allSettled([
      fetchLearningMetrics(30),
      fetchLearningTopPatterns(20, 3),
      fetchLearningQualityIssues(),
    ]);
    const errs: string[] = [];
    if (mRes.status === 'fulfilled') setMetrics(mRes.value);
    else errs.push(`metrics: ${mRes.reason instanceof Error ? mRes.reason.message : String(mRes.reason)}`);
    if (pRes.status === 'fulfilled') setTopPatterns(pRes.value.patterns || []);
    else errs.push(`top_patterns: ${pRes.reason instanceof Error ? pRes.reason.message : String(pRes.reason)}`);
    if (qRes.status === 'fulfilled') {
      setIssues({
        high_rejection_rate: qRes.value.high_rejection_rate || [],
        stale_patterns: qRes.value.stale_patterns || [],
        low_confidence_high_sample: qRes.value.low_confidence_high_sample || [],
      });
    } else {
      errs.push(`quality_issues: ${qRes.reason instanceof Error ? qRes.reason.message : String(qRes.reason)}`);
    }
    if (errs.length) setError(errs.join(' / '));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRebuild = useCallback(async () => {
    setRebuilding(true);
    setError(null);
    setInfo(null);
    try {
      const r = await postMonthlyPatternRebuild();
      setInfo(`月次 rebuild 完了: ${r.rebuilt_count} patterns / ${r.elapsed_ms}ms / high_conf ${r.post.high_conf}`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRebuilding(false);
    }
  }, [load]);

  const summary = metrics?.summary;
  const coverage = metrics?.coverage;
  const audit = metrics?.audit_violations;
  const stats = metrics?.pattern_stats;

  const coverageRate = useMemo(() => {
    if (!coverage?.total_registered) return 0;
    return (coverage.with_learning_event || 0) / coverage.total_registered;
  }, [coverage]);

  return (
    <>
      <Note>銀行取引 (wallet_txn) bank_pattern_index ベース 学習</Note>

      {error && <MessageBlock error mb={0.5}>{error}</MessageBlock>}
      {info && <MessageBlock success mb={0.5}>{info}</MessageBlock>}

      {/* 4 KPI summary */}
      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '12px 8px', display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'center' }}>
          <KpiBlock
            label="学習カバレッジ"
            value={coverage ? `${coverage.with_learning_event || 0} / ${coverage.total_registered || 0}` : '—'}
            sub={coverage ? `${(coverageRate * 100).toFixed(1)}%` : ''}
            color="#338650"
          />
          <KpiBlock
            label="AI skip 率 (30日)"
            value={summary?.ai_skip_rate_recent != null ? `${(summary.ai_skip_rate_recent * 100).toFixed(0)}%` : '—'}
            sub={summary ? `${summary.mechanical_decisions_recent} / ${summary.total_stage_decisions_recent}` : ''}
            color="#7c5cff"
          />
          <KpiBlock
            label="節約 token (30日)"
            value={summary ? `${(summary.token_saved_recent || 0).toLocaleString()}` : '—'}
            sub={summary ? `≈ $${summary.token_cost_saved_usd_recent}` : ''}
            color="#338650"
          />
          <KpiBlock
            label="patterns / 高信頼"
            value={stats ? `${stats.total_patterns || 0} / ${stats.mid_conf || 0}` : '—'}
            sub={stats ? `平均 ${formatPct(stats.avg_conf)}` : ''}
            color="#2e7cf6"
          />
          {audit && (audit.rule50_skips || 0) + (audit.rule51_skips || 0) > 0 && (
            <KpiBlock
              label="audit 違反"
              value={`${(audit.rule50_skips || 0) + (audit.rule51_skips || 0)}`}
              sub={`R50:${audit.rule50_skips || 0} / R51:${audit.rule51_skips || 0}`}
              color="#c33939"
            />
          )}
          <div style={{ marginLeft: 'auto' }}>
            <Button appearance="secondary" onClick={onRebuild} disabled={rebuilding || loading}>
              {rebuilding ? <InlineSpinner isLoading={true} /> : '月次 rebuild'}
            </Button>
          </div>
        </div>
      </CardBase>

      {/* sub-tab */}
      <CardBase paddingSize="small" mb={0.5}>
        <TabBar
          tabs={['Top Patterns', '品質問題', '手動学習 (A6)']}
          currentTabIndex={tab}
          onClickTab={(i) => setTab(i)}
          small
        />
      </CardBase>

      {tab === 0 && (
        <CardBase paddingSize="small" mb={0.5}>
          <div style={{ padding: '8px 12px' }}>
            <SubSectionTitle headlineLevel={3}>高信頼 Top Patterns ({topPatterns.length})</SubSectionTitle>
            <div style={{ marginTop: '8px', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                <thead>
                  <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                    <th style={th}>token</th>
                    <th style={th}>walletable</th>
                    <th style={th}>side</th>
                    <th style={th}>amount</th>
                    <th style={th}>partner</th>
                    <th style={{ ...th, textAlign: 'right' }}>conf</th>
                    <th style={{ ...th, textAlign: 'right' }}>samples</th>
                    <th style={{ ...th, textAlign: 'right' }}>approve</th>
                    <th style={{ ...th, textAlign: 'right' }}>reject</th>
                    <th style={th}>last</th>
                  </tr>
                </thead>
                <tbody>
                  {topPatterns.map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f0eeec' }}>
                      <td style={td}><code>{p.description_token_key}</code></td>
                      <td style={td}>{p.walletable_id}</td>
                      <td style={td}>{p.entry_side}</td>
                      <td style={td}>{p.amount_band}</td>
                      <td style={td}>{p.best_partner_name || '—'}</td>
                      <td style={{ ...td, textAlign: 'right', color: p.ai_confidence >= 0.85 ? '#338650' : '#dd7a00' }}>
                        {(p.ai_confidence * 100).toFixed(0)}%
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>{p.sample_count}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#338650' }}>{p.approved_count}</td>
                      <td style={{ ...td, textAlign: 'right', color: p.rejected_count > 0 ? '#c33939' : '#8a8580' }}>{p.rejected_count}</td>
                      <td style={{ ...td, fontSize: '0.6875rem', color: '#6b6560' }}>{p.last_confirmed_at?.slice(0, 10) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </CardBase>
      )}

      {tab === 1 && issues && (
        <>
          <IssuesTable
            title={`高拒否率 (${issues.high_rejection_rate.length})`}
            data={issues.high_rejection_rate}
            extraHeaders={['reject_rate']}
            extractExtra={(i) => [i.reject_rate ? `${((i.reject_rate || 0) * 100).toFixed(0)}%` : '—']}
            emptyText="高拒否率の pattern なし (= 学習品質良好)"
          />
          <IssuesTable
            title={`Stale (${issues.stale_patterns.length})`}
            data={issues.stale_patterns}
            extraHeaders={['last_confirmed']}
            extractExtra={(i) => [i.last_confirmed_at?.slice(0, 10) || '—']}
            emptyText="90日 未確定 pattern なし"
          />
          <IssuesTable
            title={`低信頼 + 高サンプル (${issues.low_confidence_high_sample.length})`}
            data={issues.low_confidence_high_sample}
            extraHeaders={['conf']}
            extractExtra={(i) => [`${(i.ai_confidence * 100).toFixed(0)}%`]}
            emptyText="低信頼 pattern なし"
          />
        </>
      )}

      {tab === 2 && <ManualIngestionForm onDone={load} />}
    </>
  );
};

const th: React.CSSProperties = { padding: '6px 8px', fontSize: '0.6875rem', color: '#6b6560', fontWeight: 500 };
const td: React.CSSProperties = { padding: '6px 8px', verticalAlign: 'top' };

const KpiBlock: React.FC<{ label: string; value: string; sub?: string; color: string }> = ({ label, value, sub, color }) => (
  <div style={{ display: 'flex', flexDirection: 'column', minWidth: '120px' }}>
    <div style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: '1rem', fontWeight: 600, color }}>{value}</div>
    {sub && <div style={{ fontSize: '0.6875rem', color: '#8a8580' }}>{sub}</div>}
  </div>
);

const IssuesTable: React.FC<{
  title: string;
  data: LearningQualityIssue[];
  extraHeaders: string[];
  extractExtra: (i: LearningQualityIssue) => string[];
  emptyText: string;
}> = ({ title, data, extraHeaders, extractExtra, emptyText }) => (
  <CardBase paddingSize="small" mb={0.5}>
    <div style={{ padding: '8px 12px' }}>
      <SubSectionTitle headlineLevel={4}>{title}</SubSectionTitle>
      {data.length === 0 ? (
        <Note>{emptyText}</Note>
      ) : (
        <div style={{ marginTop: '8px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
            <thead>
              <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                <th style={th}>token</th>
                <th style={th}>walletable</th>
                <th style={th}>side / amount</th>
                <th style={th}>partner</th>
                <th style={{ ...th, textAlign: 'right' }}>samples</th>
                {extraHeaders.map((h, i) => <th key={i} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.map((i, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #f0eeec' }}>
                  <td style={td}><code>{i.description_token_key}</code></td>
                  <td style={td}>{i.walletable_id}</td>
                  <td style={td}>{i.entry_side} / {i.amount_band}</td>
                  <td style={td}>{i.best_partner_name || '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{i.sample_count}</td>
                  {extractExtra(i).map((v, j) => (
                    <td key={j} style={td}>{v}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </CardBase>
);

const ManualIngestionForm: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const [mode, setMode] = useState<'structured' | 'free_text'>('structured');
  const [token, setToken] = useState('');
  const [walletableId, setWalletableId] = useState('');
  const [entrySide, setEntrySide] = useState<'income' | 'expense'>('expense');
  const [amountBand, setAmountBand] = useState<'micro' | 'small' | 'medium' | 'large'>('small');
  const [partnerId, setPartnerId] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [accountItemId, setAccountItemId] = useState('');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validationError = useMemo(() => {
    if (mode === 'structured') {
      if (!token || token.length < 2) return 'token (2 文字以上) が必須';
      if (!walletableId || isNaN(Number(walletableId))) return 'walletable_id が必須';
      if (!['income', 'expense'].includes(entrySide)) return 'entry_side が不正';
      if (!['micro', 'small', 'medium', 'large'].includes(amountBand)) return 'amount_band が不正';
      return null;
    }
    if (mode === 'free_text') {
      if (text.length < 5) return 'テキスト 5 文字以上必須';
      if (text.length > 2000) return `テキスト 2000 文字以下必須 (現 ${text.length})`;
      return null;
    }
    return null;
  }, [mode, token, walletableId, entrySide, amountBand, text]);

  const submit = async () => {
    if (validationError) {
      setSubmitError(validationError);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    try {
      const body = mode === 'structured'
        ? {
            mode: 'structured' as const,
            token,
            walletable_id: Number(walletableId),
            entry_side: entrySide,
            amount_band: amountBand,
            partner_id: partnerId ? Number(partnerId) : null,
            partner_name: partnerName || null,
            section_id: sectionId ? Number(sectionId) : null,
            account_item_id: accountItemId ? Number(accountItemId) : null,
          }
        : {
            mode: 'free_text' as const,
            text,
          };
      const r = await postLearningChatIngest(body);
      setResult(r);
      onDone();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CardBase paddingSize="small" mb={0.5}>
      <div style={{ padding: '12px' }}>
        <SubSectionTitle headlineLevel={3}>手動学習 (A6: chat-to-learning)</SubSectionTitle>
        <Note>
          パターンを直接 bank_pattern_index に追加します。 構造化モード = 即時反映 (機械的)。 自由文モード = Claude が解析後反映 (非同期)。
        </Note>

        <div style={{ marginTop: '12px', marginBottom: '12px' }}>
          <SelectBox
            options={[
              { name: '構造化入力 (= 直接 INSERT)', value: 'structured' },
              { name: '自由文 (= AI parse 経由)', value: 'free_text' },
            ]}
            value={mode}
            onChange={(e) => setMode(e.target.value as any)}
          />
        </div>

        {mode === 'structured' && (
          <VStack gap={0.5}>
            <FormRow label="token (description_token_key) *">
              <TextField value={token} onChange={(e) => setToken(e.target.value)} placeholder="例: シェアダイン / ｱﾙｿｸ" />
            </FormRow>
            <FormRow label="walletable_id *">
              <TextField value={walletableId} onChange={(e) => setWalletableId(e.target.value)} placeholder="例: 2122229" />
            </FormRow>
            <FormRow label="entry_side *">
              <SelectBox
                options={[
                  { name: 'expense (支出)', value: 'expense' },
                  { name: 'income (収入)', value: 'income' },
                ]}
                value={entrySide}
                onChange={(e) => setEntrySide(e.target.value as any)}
              />
            </FormRow>
            <FormRow label="amount_band *">
              <SelectBox
                options={[
                  { name: 'micro (≤1万)', value: 'micro' },
                  { name: 'small (≤10万)', value: 'small' },
                  { name: 'medium (≤100万)', value: 'medium' },
                  { name: 'large (>100万)', value: 'large' },
                ]}
                value={amountBand}
                onChange={(e) => setAmountBand(e.target.value as any)}
              />
            </FormRow>
            <FormRow label="partner_id (D1 実在 必須)">
              <TextField value={partnerId} onChange={(e) => setPartnerId(e.target.value)} placeholder="例: 87441099" />
            </FormRow>
            <FormRow label="partner_name (任意)">
              <TextField value={partnerName} onChange={(e) => setPartnerName(e.target.value)} placeholder="例: 株式会社シェアダイン" />
            </FormRow>
            <FormRow label="section_id (D1 実在 必須、 任意)">
              <TextField value={sectionId} onChange={(e) => setSectionId(e.target.value)} placeholder="例: 3068547" />
            </FormRow>
            <FormRow label="account_item_id (任意)">
              <TextField value={accountItemId} onChange={(e) => setAccountItemId(e.target.value)} placeholder="例: 858355845" />
            </FormRow>
          </VStack>
        )}

        {mode === 'free_text' && (
          <TextArea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="例: 株式会社シェアダインは三井住友VISAカードで支出。 部門は信州屋新宿、 科目は会議費"
            height={120}
          />
        )}

        {validationError && (
          <Note mt={0.5}>{validationError}</Note>
        )}
        <div style={{ marginTop: '12px' }}>
          <Button appearance="primary" onClick={submit} disabled={submitting || !!validationError}>
            {submitting ? <InlineSpinner isLoading={true} /> : '送信'}
          </Button>
        </div>

        {submitError && <MessageBlock error mt={0.5}>{submitError}</MessageBlock>}
        {result && <MessageBlock success mt={0.5}>登録完了: {JSON.stringify(result).slice(0, 200)}</MessageBlock>}
      </div>
    </CardBase>
  );
};

const FormRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div style={{ fontSize: '0.75rem', color: '#6b6560', marginBottom: '4px', fontWeight: 500 }}>{label}</div>
    {children}
  </div>
);
